import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, BadgeCheck, Globe, Bell } from "lucide-react";
import { LAWYER_BASE } from "@lawyer/constants/route";
import CompleteProfileBanner from "@lawyer/components/CompleteProfileBanner";
import VerificationProgressCard from "@lawyer/components/VerificationProgressCard";
import ListedBanner from "@lawyer/components/ListedBanner";
import VerifiedAdvocateBadge from "@user/components/VerifiedAdvocateBadge";
import MobileStatusCard, { useNextAvailable } from "@lawyer/components/mobile/MobileStatusCard";
import { NextAvailabilityCard, InfoCard, NextConsultationCard, RestOfTodayCard } from "@lawyer/components/mobile/MobileExtras";
import { MobileTodayList, MobileEarningsCard } from "@lawyer/components/mobile/MobileDashboardSections";
import { TodaySummaryCard, EarningsSummaryCard, PerformanceSummaryCard } from "@lawyer/components/DashboardSummaryCards";
import { fmtIstDate } from "@lawyer/components/mobile/statusUtils";
import { photoSrc } from "@shared/photoSrc";

export default function LawyerDashboard({ data, onReload, onAvailabilityToggle, onVacationToggle }) {
  // `data` is provided by LawyerApp shell so the sidebar status reflects same fetch.
  // The shell keeps it across routes; coming back here shows it instantly and revalidates in the background.
  const hadData = useRef(!!data);
  useEffect(() => { if (hadData.current) onReload?.(); }, [onReload]);
  // This-month earnings come from the dashboard payload (server-derived, IST month) — no separate earnings fetch.
  const monthPaise = data?.earnings?.month_earned_paise ?? null;
  const lw = data?.lawyer;
  const state = lw ? (lw.availability?.state || (lw.vacation_mode ? "vacation" : lw.available ? "online_manual" : "offline")) : null;
  // Phone "Next Availability" card (online/offline only — never during vacation). Also feeds the offline status card.
  // Served by /lawyer/dashboard (`lawyer.next_available`, null when none); the hook only fetches when the key is absent.
  const nextAvailable = useNextAvailable(lw?.id, !!lw && lw.listed !== false && (state === "offline" || state === "online_scheduled" || state === "online_manual"), lw?.next_available);
  if (!data) return <div className="text-slate-500" data-testid="lawyer-dashboard-loading">Loading…</div>;

  const { lawyer, counts, today_agenda, earnings, performance } = data;
  const LIVE = ["live", "waiting_for_user", "waiting_for_lawyer"];
  const upcomingToday = today_agenda.filter((c) => !LIVE.includes(c.status) && !["completed", "no_show"].includes(c.status));
  const online = state === "online_scheduled" || state === "online_manual";
  // Bumped whenever we re-fetch the dashboard so the readiness widget
  // pulls fresh state (e.g. after admin approves KYC).
  const readinessRefreshKey = data.__reload_id || `${lawyer.verified}-${lawyer.available}-${counts.completed}`;
  const initials = (lawyer.name || "L").replace(/^Adv\.\s*/, "").split(" ").slice(0, 2).map((x) => x[0]).join("").toUpperCase();

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6" data-testid="lawyer-dashboard-header">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
            Hi, {lawyer.name?.replace(/^Adv\.\s*/, "").split(" ")[0] || "Counsel"}
          </h1>
          <p className="mt-1 text-sm text-slate-500 inline-flex items-center gap-2 flex-wrap">
            {lawyer.verified && lawyer.verification_all_met !== false
              ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs font-bold" data-testid="dashboard-tag-verified"><BadgeCheck size={13} /> Verified</span>
              : <span className="inline-flex items-center gap-1 text-amber-700" data-testid="dashboard-tag-verification-pending"><AlertCircle size={14} /> {lawyer.verification_percent != null ? `Verification ${lawyer.verification_percent}%` : "Verification pending"}</span>}
            {lawyer.listed && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#FBF4DF] text-[#0A2540] border border-[#D4AF37]/50 px-2 py-0.5 text-xs font-bold" data-testid="dashboard-tag-listed"><Globe size={13} className="text-[#8a6d1a]" /> Listed</span>
            )}
            <span>·</span>
            <span>{lawyer.specialization} · {lawyer.city}</span>
            <VerifiedAdvocateBadge lawyer={lawyer} size="sm" />
          </p>
        </div>
        <div className="hidden lg:flex items-center gap-3 self-start">
          <Link to={`${LAWYER_BASE}/consultations?bucket=upcoming`} aria-label="Upcoming bookings" className="relative h-10 w-10 grid place-items-center rounded-full border border-slate-200 bg-white text-[#0A2540] hover:border-[#0A2540]" data-testid="d-bell">
            <Bell size={18} />
            {counts.upcoming > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-extrabold grid place-items-center" data-testid="d-bell-badge">
                {counts.upcoming > 9 ? "9+" : counts.upcoming}
              </span>
            )}
          </Link>
          <Link to={`${LAWYER_BASE}/profile`} aria-label="Profile" className="h-10 w-10 rounded-full bg-[#D4AF37] text-[#0A2540] grid place-items-center text-xs font-extrabold overflow-hidden" data-testid="d-avatar">
            {lawyer.photo ? <img src={photoSrc(lawyer.photo, "sm")} alt="" className="h-full w-full object-cover" /> : initials}
          </Link>
        </div>
      </div>

      {lawyer.show_listed_banner && <ListedBanner available={lawyer.available} onDone={onReload} />}

      {/* Verification Progress — canonical 10-requirement matrix (same as Admin). Hidden once everything is
          met AND the lawyer is listed; the full matrix stays available on the Verification page. */}
      {!(lawyer.verification_all_met && lawyer.listed) && (
        <div className="mb-6" data-testid="dashboard-verification-progress">
          <VerificationProgressCard refreshKey={readinessRefreshKey} initialData={data.verification || null} />
        </div>
      )}

      {/* Only while client-visible profile fields are empty; hidden once the profile is complete. */}
      {lawyer.profile_missing?.length > 0 && (
        <div className="mb-8">
          <CompleteProfileBanner missing={lawyer.profile_missing} />
        </div>
      )}

      {/* Phone dashboard: status → today → earnings. Desktop keeps the existing layout below. */}
      <div className="lg:hidden space-y-4" data-testid="m-dashboard" data-state={state}>
        <MobileStatusCard lawyer={lawyer} availability={lawyer.availability} todayAgenda={today_agenda} nextAvailable={nextAvailable}
          onAvailabilityToggle={onAvailabilityToggle} onVacationToggle={onVacationToggle} />

        {state === "vacation" && (
          <InfoCard tone="blue" testid="m-vacation-note" title="You are on vacation"
            body="Your profile is visible to clients, but you cannot be booked while you are on vacation. Bookings reopen only when you end vacation mode." />
        )}

        {state === "busy" ? (
          <>
            <MobileTodayList agenda={upcomingToday} state={state} title="Upcoming After This" emptyTitle="Nothing else today" />
            <RestOfTodayCard count={Math.max(0, upcomingToday.length - 4)} />
          </>
        ) : (
          <>
            <MobileTodayList agenda={today_agenda} state={state} />
            {online && upcomingToday.length > 0 && <NextConsultationCard consult={upcomingToday[0]} />}
          </>
        )}

        {(online || state === "offline") && lawyer.listed !== false && (online ? today_agenda.length === 0 : true) && (
          <NextAvailabilityCard next={nextAvailable} lawyerId={lawyer.id} />
        )}
        {online && today_agenda.length === 0 && (
          <InfoCard tone="emerald" testid="m-online-tip" title="Stay online to get instant bookings" body="More visibility leads to more consultations." />
        )}
        {state === "offline" && (
          <InfoCard tone="amber" testid="m-offline-tip" title="Go online to receive instant bookings" body="Clients can book you for instant consultations when you are online." />
        )}

        <MobileEarningsCard monthPaise={monthPaise} fallbackPaise={earnings.total_earned_paise} />
      </div>

      {/* Desktop: state card + Today's Summary → Today's Consultations → Earnings + Performance. Same data/handlers as mobile. */}
      <div className="hidden lg:block space-y-6" data-testid="desktop-dashboard">
        <div className="grid grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-5 items-stretch">
          <MobileStatusCard wide lawyer={lawyer} availability={lawyer.availability} todayAgenda={today_agenda} nextAvailable={nextAvailable}
            onAvailabilityToggle={onAvailabilityToggle} onVacationToggle={onVacationToggle} />
          <TodaySummaryCard counts={counts} todayAgenda={today_agenda} performance={performance} monthPaise={monthPaise} fallbackPaise={earnings.total_earned_paise} />
        </div>
        <MobileTodayList wide agenda={today_agenda} state={state}
          nextAvailableText={lawyer.vacation_mode && lawyer.vacation_until ? fmtIstDate(lawyer.vacation_until) : null} />
        <div className="grid grid-cols-2 gap-5 items-start">
          <EarningsSummaryCard earnings={earnings} monthPaise={monthPaise} />
          <PerformanceSummaryCard performance={performance} counts={counts} />
        </div>
      </div>
    </>
  );
}
